import { useState, type FormEvent } from "react"
import { Head, Link, router } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { GreenScoreBadge, GreenScoreBreakdown } from "@/components/public/green-score-badge"
import type { SeoMeta } from "@/types"
import type {
  GreenScore,
  AmenityData,
  FloodData,
  PlanningData,
} from "@/types/public-data"

/* ------------------------------------------------------------------ */
/* Raw shapes from Rails (snake_case keys)                             */
/* ------------------------------------------------------------------ */

interface RawGreenScore {
  total: number
  breakdown: {
    green_space: number
    flood_risk: number
    conservation: number
    walkability: number
    cycling: number
  }
}

interface RawFlood {
  stations: Array<{ label: string; river_name?: string; status?: string }>
  station_count: number
  has_active_warnings: boolean
}

interface RawAmenities {
  schools: Array<{ name: string; type?: string }>
  parks: Array<{ name: string }>
  restaurants: Array<{ name: string; cuisine?: string }>
  shops: number
  transport_stops: number
  cycleways: number
}

interface RawPlanning {
  conservation_areas: number
  green_belt: boolean | number
  listed_buildings: number
}

/* ------------------------------------------------------------------ */
/* Mappers: snake_case -> camelCase                                     */
/* ------------------------------------------------------------------ */

function mapGreenScore(raw: RawGreenScore | null): GreenScore | null {
  if (!raw) return null
  return {
    total: raw.total,
    breakdown: {
      greenSpace: raw.breakdown.green_space,
      floodRisk: raw.breakdown.flood_risk,
      conservation: raw.breakdown.conservation,
      walkability: raw.breakdown.walkability,
      cycling: raw.breakdown.cycling,
    },
  }
}

function mapFlood(raw: RawFlood | null): FloodData | null {
  if (!raw) return null
  return {
    stations: raw.stations.map((s) => ({
      label: s.label,
      riverName: s.river_name,
      status: s.status,
    })),
    stationCount: raw.station_count,
    hasActiveWarnings: raw.has_active_warnings,
  }
}

function mapAmenities(raw: RawAmenities | null): AmenityData | null {
  if (!raw) return null
  return {
    schools: raw.schools,
    parks: raw.parks,
    restaurants: raw.restaurants,
    shops: raw.shops,
    transportStops: raw.transport_stops,
    cycleways: raw.cycleways,
  }
}

function mapPlanning(raw: RawPlanning | null): PlanningData | null {
  if (!raw) return null
  return {
    conservationAreas: raw.conservation_areas,
    greenBelt: typeof raw.green_belt === "boolean"
      ? (raw.green_belt ? 1 : 0)
      : raw.green_belt,
    listedBuildings: raw.listed_buildings,
  }
}

/* ------------------------------------------------------------------ */
/* Factor explanation data                                             */
/* ------------------------------------------------------------------ */

const FACTORS = [
  {
    name: "Green space",
    weight: "35%",
    description: "Number of parks and green spaces within one mile, scored against a benchmark of 10.",
  },
  {
    name: "Conservation",
    weight: "20%",
    description: "Conservation areas, green belt designation, and listed buildings contribute to environmental protection.",
  },
  {
    name: "Walkability",
    weight: "20%",
    description: "Shops, restaurants, and transport stops within half a mile, reflecting how walkable the area is.",
  },
  {
    name: "Flood resilience",
    weight: "15%",
    description: "Fewer flood monitoring stations and no active warnings indicate lower flood risk.",
  },
  {
    name: "Cycling",
    weight: "10%",
    description: "Dedicated cycleways nearby, encouraging low-carbon transport.",
  },
]

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

interface PageProps {
  meta: SeoMeta
  postcode: string
  greenScore: RawGreenScore | null
  amenities: RawAmenities | null
  flood: RawFlood | null
  planning: RawPlanning | null
}

export default function GreenScorePage({
  meta,
  postcode,
  greenScore: rawGreenScore,
  amenities: rawAmenities,
  flood: rawFlood,
  planning: rawPlanning,
}: PageProps) {
  const [methodOpen, setMethodOpen] = useState(false)
  const [searchPostcode, setSearchPostcode] = useState("")
  const [searchLoading, setSearchLoading] = useState(false)

  function handleSearch(e: FormEvent) {
    e.preventDefault()
    const cleaned = searchPostcode.trim().toUpperCase().replace(/\s+/g, "")
    if (cleaned.length === 0) return
    setSearchLoading(true)
    router.get(`/tools/green-score/${cleaned}`, {}, {
      onFinish: () => setSearchLoading(false),
    })
  }

  const greenScore = mapGreenScore(rawGreenScore)
  const amenities = mapAmenities(rawAmenities)
  const flood = mapFlood(rawFlood)
  const planning = mapPlanning(rawPlanning)

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Hero */}
      <section className="bg-cream px-6 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <Link
            href="/tools/postcode-report"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-red transition-colors hover:text-deep-red"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
              <path
                fillRule="evenodd"
                d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z"
                clipRule="evenodd"
              />
            </svg>
            Try another postcode
          </Link>

          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl md:text-5xl">
            Green Living Score
            <span className="text-red">.</span>
          </h1>
          <p className="mt-3 text-lg text-slate">
            {postcode}
          </p>
        </div>
      </section>

      {/* Quick search */}
      <section className="border-t border-stone px-6 py-6">
        <div className="mx-auto max-w-4xl">
          <form onSubmit={handleSearch} className="flex max-w-md gap-3">
            <input
              type="text"
              value={searchPostcode}
              onChange={(e) => setSearchPostcode(e.target.value)}
              placeholder="Search another postcode"
              className="flex-1 rounded-lg border border-stone bg-white px-3 py-2 text-sm text-charcoal placeholder:text-slate/50 focus:border-charcoal focus:outline-none"
              aria-label="UK postcode"
            />
            <button
              type="submit"
              disabled={searchLoading}
              className="rounded-lg bg-charcoal px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate disabled:opacity-60"
            >
              {searchLoading ? "Searching..." : "Search"}
            </button>
          </form>
        </div>
      </section>

      {/* Main score card */}
      {greenScore ? (
        <section className="border-t border-stone px-6 py-12">
          <div className="mx-auto max-w-4xl">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="flex flex-col items-center gap-8 md:flex-row md:items-start">
                  <GreenScoreBadge score={greenScore} size="lg" />
                  <div className="flex-1">
                    <h2 className="font-display text-xl font-bold text-charcoal">
                      Score breakdown
                    </h2>
                    <div className="mt-4">
                      <GreenScoreBreakdown score={greenScore} />
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      ) : (
        <section className="border-t border-stone px-6 py-12">
          <div className="mx-auto max-w-4xl">
            <div className="rounded-xl border border-stone bg-white p-6 text-center md:p-8">
              <p className="text-lg font-medium text-charcoal">
                Unable to calculate green score
              </p>
              <p className="mt-2 text-sm text-slate">
                We could not gather enough environmental data for {postcode} to produce a
                score. This usually means one or more data sources were unavailable.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Supporting data cards */}
      <section className="border-t border-stone bg-cream px-6 py-12">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal stagger className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-stone bg-white p-5">
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                Parks nearby
              </p>
              <p className="mt-2 font-display text-3xl font-bold text-charcoal">
                {amenities?.parks?.length ?? "N/A"}
              </p>
              <p className="mt-1 text-xs text-slate">within 1 mile</p>
            </div>

            <div className="rounded-xl border border-stone bg-white p-5">
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                Flood stations
              </p>
              <p className="mt-2 font-display text-3xl font-bold text-charcoal">
                {flood?.stationCount ?? "N/A"}
              </p>
              <p className="mt-1 text-xs text-slate">
                {flood?.hasActiveWarnings ? "Active warnings" : "No active warnings"}
              </p>
            </div>

            <div className="rounded-xl border border-stone bg-white p-5">
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                Conservation areas
              </p>
              <p className="mt-2 font-display text-3xl font-bold text-charcoal">
                {planning?.conservationAreas ?? "N/A"}
              </p>
              <p className="mt-1 text-xs text-slate">within the area</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* How we calculate this */}
      <section className="border-t border-stone px-6 py-12">
        <div className="mx-auto max-w-4xl">
          <button
            type="button"
            onClick={() => setMethodOpen((prev) => !prev)}
            className="flex w-full items-center justify-between text-left"
          >
            <h2 className="font-display text-xl font-bold text-charcoal">
              How we calculate this
              <span className="text-red">.</span>
            </h2>
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className={`size-5 text-slate transition-transform duration-200 ${methodOpen ? "rotate-180" : ""}`}
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {methodOpen && (
            <div className="mt-6 space-y-4">
              <p className="text-sm leading-relaxed text-slate">
                The Green Living Score combines five environmental and
                sustainability factors into a single number out of 100. Each factor is
                scored individually and weighted to reflect its relative importance.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {FACTORS.map((factor) => (
                  <div
                    key={factor.name}
                    className="rounded-lg border border-stone bg-cream/50 p-4"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-charcoal">
                        {factor.name}
                      </p>
                      <span className="rounded-full bg-charcoal/5 px-2 py-0.5 text-xs font-medium text-charcoal">
                        {factor.weight}
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-slate">
                      {factor.description}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate">
                Data sources: OpenStreetMap (Overpass), Environment Agency,
                Planning Data (DLUHC).
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Action buttons */}
      <section className="border-t border-stone bg-cream px-6 py-12">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <Link
            href={`/tools/postcode-report/${postcode.replace(/\s+/g, "")}`}
            className="inline-flex w-full items-center justify-center rounded-lg bg-charcoal px-6 py-3 text-base font-medium text-white transition-colors hover:bg-slate sm:w-auto"
          >
            Full postcode report
          </Link>
          <Link
            href="/tools/postcode-report"
            className="inline-flex w-full items-center justify-center rounded-lg border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-cream sm:w-auto"
          >
            Try another postcode
          </Link>
        </div>
      </section>

      {/* Data sources */}
      <section className="border-t border-stone px-6 py-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-center text-xs text-slate">
            Data sources: OpenStreetMap (Overpass), Environment Agency, Planning Data (DLUHC).
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
